/**
 * Izin / Sakit / Cuti — inventaris A17 (Q35 status kehadiran, Q37 pengajuan → Kepala Cabang memutus, K125 personil
 * tanpa login diajukan atas nama oleh KC/Owner). Tombol putus mengikuti jawaban backend; layar tidak menebak hak.
 * BUSINESS-34 (26 Sep 2026): jatah cuti 12 hari kerja/tahun; sakit ≥ 2 hari wajib unggah surat dokter sebelum disetujui.
 * BUSINESS-44 (26 Sep 2026): izin setengah hari — jenis Izin, satu tanggal; dihitung 0,5 hari izin.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { mintaTeks } from "../lib/dialogAplikasi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { formatDate } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";
import PemilihCari from "../components/etams/PemilihCari";
import { cariKaryawan, labelKaryawan, ketKaryawan } from "../lib/sumberPilihan";

const JENIS = ["IZIN", "SAKIT", "CUTI"];
const JATAH_CUTI = 12;
const SURAT_MIME = "image/jpeg,image/png,image/webp,application/pdf";
const butuhSurat = (x) => x.jenis === "SAKIT" && x.tanggal_selesai !== x.tanggal_mulai;   // ≥ 2 hari kalender
const KATA_STATUS = { DIAJUKAN: "Menunggu keputusan", DISETUJUI: "Disetujui", DITOLAK: "Ditolak", DIBATALKAN: "Dibatalkan" };
const KATA_JENIS = { IZIN: "Izin", SAKIT: "Sakit", CUTI: "Cuti" };
//: Cermin `URUT_SAH` di backend/services/pengajuan_izin.py.
const URUT_SAH = ["created_at", "tanggal_mulai", "status", "jenis"];
const KOLOM_CSV = [
  { kunci: "karyawan", judul: "Karyawan", ambil: (x) => x.employee?.full_name || "" },
  { kunci: "jenis", judul: "Jenis", ambil: (x) => `${KATA_JENIS[x.jenis] || x.jenis}${x.setengah_hari ? " setengah hari" : ""}` },
  { kunci: "tanggal_mulai", judul: "Dari" },
  { kunci: "tanggal_selesai", judul: "Sampai" },
  { kunci: "status", judul: "Status", ambil: (x) => KATA_STATUS[x.status] || x.status },
  { kunci: "alasan", judul: "Alasan" },
];

export default function HRPengajuanIzin() {
  const { profile, roleCode } = useAuth();
  const [baris, setBaris] = useState(null);
  const [total, setTotal] = useState(0);
  const [karyawan, setKaryawan] = useState(null);   // objek karyawan terpilih (PemilihCari) / diri sendiri
  const [belumTertaut, setBelumTertaut] = useState(false);
  const [form, setForm] = useState({ employee_id: "", jenis: "IZIN", tanggal_mulai: "", tanggal_selesai: "", alasan: "", setengah_hari: false });
  const [sibuk, setSibuk] = useState(false);
  const bolehAtasNama = roleCode === "OWNER" || roleCode === "KEPALA_CABANG";

  // Pola daftar seragam (28 Sep 2026): pengajuan izin tumbuh tiap hari — halaman, cari, dan urut di SERVER.
  // Saringan bawaan untuk pemutus: "Menunggu keputusan" (chip bisa dihapus untuk melihat semuanya).
  const daftar = useDaftar(baris || [], {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: bolehAtasNama ? "DIAJUKAN" : "", tampil: (v) => KATA_STATUS[v] || v },
      jenis: { label: "Jenis", param: "jenis", tampil: (v) => KATA_JENIS[v] || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({ ...paramSaringan, ...(cari && { q: cari }),
    limit, offset, sort, arah, ...o }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/hr/pengajuan-izin", { params: bangunParams({ offset: off, limit: n }) })).data, [bangunParams]);

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/hr/pengajuan-izin", { params: bangunParams() });
      setBaris(r.data.items || []);
      setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar pengajuan gagal dimuat.")); setBaris([]); setTotal(0); }
  }, [bangunParams]);
  useEffect(() => { muat(); }, [muat]);

  // Owner/KC memilih karyawan lewat PemilihCari (cari di server) — dulu seluruh karyawan dimuat ke satu
  // dropdown. Staf lain hanya bisa mengajukan untuk dirinya sendiri.
  useEffect(() => {
    if (bolehAtasNama) return;
    (async () => {
      try {
        const r = await api.get("/hr/attendance/saya/hari-ini");
        const saya = r.data.employee;
        if (saya) { setKaryawan(saya); setForm((f) => ({ ...f, employee_id: saya.id })); }
        else setBelumTertaut(true);
      } catch (e) {
        // 404 = akun belum ditautkan ke data karyawan (pintu yang sama dengan layar Koreksi Absen) — bukan galat.
        if (e?.response?.status === 404) setBelumTertaut(true);
        else toast.error(apiErrorMessage(e, "Data karyawan gagal dimuat."));
      }
    })();
  }, [bolehAtasNama]);

  const kirim = async () => {
    setSibuk(true);
    try {
      const setengah = form.jenis === "IZIN" && form.setengah_hari;
      const r = await api.post("/hr/pengajuan-izin", {
        ...form, setengah_hari: setengah,
        tanggal_selesai: setengah ? form.tanggal_mulai : (form.tanggal_selesai || form.tanggal_mulai),
      });
      // K203 / VONIS-27SEP-BUSINESS-56-65 (BUSINESS-61): izin Owner tanpa Owner lain tercatat langsung; tanggal di
      // periode gaji yang sudah final → selisihnya masuk gaji periode berikutnya.
      if (r.data?.tercatat_langsung) toast.success("Izin tercatat langsung, karena tidak ada Owner lain yang memutus.");
      else if (r.data?.perlu_tindakan) toast.warning(r.data.perlu_tindakan);
      else toast.success("Pengajuan dikirim.");
      if (r.data?.penyesuaian_gaji) toast.info(r.data.penyesuaian_gaji);
      setForm((f) => ({ ...f, tanggal_mulai: "", tanggal_selesai: "", alasan: "", setengah_hari: false }));
      await muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Pengajuan gagal.")); }
    finally { setSibuk(false); }
  };

  const putus = async (x, setuju) => {
    let alasan = null;
    if (!setuju) {
      alasan = await mintaTeks("Alasan menolak pengajuan ini:", { minPanjang: 5 });
      if (!alasan) return;
    }
    setSibuk(true);
    try {
      const r = await api.post(`/hr/pengajuan-izin/${x.id}/putuskan`, { setuju, alasan });
      toast.success(!setuju ? "Pengajuan ditolak." : r.data.setengah_hari ? "Disetujui — izin setengah hari tercatat."
        : `Disetujui — ${(r.data.hari_tercatat || []).length} hari kerja tercatat.`);
      if (setuju && r.data?.penyesuaian_gaji) toast.info(r.data.penyesuaian_gaji);
      await muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Keputusan gagal disimpan.")); }
    finally { setSibuk(false); }
  };

  const unggahSurat = async (x, berkas) => {
    if (!berkas) return;
    const fd = new FormData();
    fd.append("file", berkas);
    setSibuk(true);
    try { await api.post(`/hr/pengajuan-izin/${x.id}/surat`, fd); toast.success("Surat dokter terunggah."); await muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Unggah surat gagal.")); }
    finally { setSibuk(false); }
  };

  const lihatSurat = async (x) => {
    const tab = window.open("", "_blank");   // dibuka DULU — sesudah await diblokir peramban
    try {
      const r = await api.get(`/hr/pengajuan-izin/${x.id}/surat`);
      if (!r.data.signed_url) throw new Error("Tautan surat tidak tersedia.");
      if (tab) tab.location.href = r.data.signed_url; else window.location.href = r.data.signed_url;
    } catch (e) { if (tab) tab.close(); toast.error(apiErrorMessage(e, "Surat gagal dibuka.")); }
  };

  const batal = async (x) => {
    setSibuk(true);
    try { await api.post(`/hr/pengajuan-izin/${x.id}/batalkan`); toast.success("Pengajuan dibatalkan."); await muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Pembatalan gagal.")); }
    finally { setSibuk(false); }
  };

  return (
    <div className="space-y-4" data-testid="izin-page">
      <div>
        <h1 className="text-xl font-semibold">Izin, Sakit &amp; Cuti</h1>
        <p className="text-sm text-muted-foreground">
          Ajukan izin untuk diri sendiri. Personil tanpa akun diajukan Kepala Cabang. Kepala Cabang region memutus;
          pengajuan Kepala Cabang diputus Owner. Yang disetujui tercatat di rekap absensi per hari kerja. Jatah cuti
          {` ${JATAH_CUTI} `}hari kerja per tahun. Sakit 2 hari atau lebih wajib disertai surat dokter (diunggah sesudah
          pengajuan dikirim, sebelum disetujui). Izin setengah hari (datang siang atau pulang cepat dengan izin) dihitung
          setengah hari izin; separuh hari lainnya tetap absen seperti biasa.
        </p>
      </div>

      <Card className="p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm space-y-1">
            <span>Karyawan</span>
            <PemilihCari value={karyawan} disabled={!bolehAtasNama}
                         onChange={(k) => { setKaryawan(k); setForm((f) => ({ ...f, employee_id: k?.id || "" })); }}
                         cari={(kata) => cariKaryawan(kata)} label={labelKaryawan} keterangan={ketKaryawan}
                         placeholder="Pilih karyawan" placeholderCari="Ketik nama atau kode karyawan…"
                         testId="izin-karyawan" />
            {belumTertaut && (
              <span className="block text-xs text-muted-foreground" data-testid="izin-belum-tertaut">
                Akun ini belum ditautkan ke data karyawan, jadi belum bisa mengajukan dari sini. Minta Kepala Cabang
                mengajukan atas nama Anda.
              </span>
            )}
          </label>
          <label className="text-sm space-y-1">
            <span>Jenis</span>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.jenis}
                    onChange={(e) => setForm({ ...form, jenis: e.target.value, setengah_hari: e.target.value === "IZIN" && form.setengah_hari })}
                    data-testid="izin-jenis">
              {JENIS.map((j) => <option key={j} value={j}>{j.charAt(0) + j.slice(1).toLowerCase()}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span>Dari tanggal</span>
            <Input type="date" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} data-testid="izin-mulai" />
          </label>
          <label className="text-sm space-y-1">
            <span>Sampai tanggal (kosong = satu hari)</span>
            <Input type="date" value={form.setengah_hari ? "" : form.tanggal_selesai} disabled={form.setengah_hari}
                   onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} data-testid="izin-selesai" />
          </label>
        </div>
        {form.jenis === "IZIN" && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.setengah_hari}
                   onChange={(e) => setForm({ ...form, setengah_hari: e.target.checked, tanggal_selesai: "" })}
                   data-testid="izin-setengah-hari" />
            <span>Setengah hari (satu tanggal — dihitung setengah hari izin)</span>
          </label>
        )}
        <Textarea placeholder="Alasan (minimal 5 karakter)" value={form.alasan}
                  onChange={(e) => setForm({ ...form, alasan: e.target.value })} data-testid="izin-alasan" />
        <Button disabled={sibuk || !form.employee_id || !form.tanggal_mulai || form.alasan.trim().length < 5}
                onClick={kirim} data-testid="izin-kirim">Ajukan</Button>
      </Card>

      <PalangDaftar daftar={daftar} placeholder="Cari nama karyawan / alasan…" testId="izin-palang"
                    ekspor={{ nama: "pengajuan_izin", kolom: KOLOM_CSV, ambilHalaman }}>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                aria-label="Saring status" data-testid="izin-filter-status">
          <option value="">Semua status</option>
          {Object.entries(KATA_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={daftar.nilaiSaringan.jenis} onChange={(e) => daftar.setSaringan("jenis", e.target.value)}
                aria-label="Saring jenis" data-testid="izin-filter-jenis">
          <option value="">Semua jenis</option>
          {JENIS.map((j) => <option key={j} value={j}>{KATA_JENIS[j]}</option>)}
        </select>
      </PalangDaftar>
      {baris === null ? <div className="text-sm text-muted-foreground">Memuat…</div> : daftar.total === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="izin-kosong">
          {daftar.cari.trim() || daftar.saringanAktif.length > 0 ? "Tidak ada yang cocok." : "Belum ada pengajuan."}
        </Card>
      ) : (
        <div className="space-y-2">
          {daftar.tampil.map((x) => (
            <Card key={x.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`izin-${x.id}`}>
              <div className="text-sm space-y-1">
                <div className="font-medium">{x.employee?.full_name || "—"} · {x.jenis}{x.setengah_hari ? " setengah hari" : ""} · {formatDate(x.tanggal_mulai)}{x.tanggal_selesai !== x.tanggal_mulai ? ` s.d. ${formatDate(x.tanggal_selesai)}` : ""}</div>
                <div className="text-muted-foreground">{KATA_STATUS[x.status] || x.status}{x.atas_nama ? " · diajukan atas nama" : ""}</div>
                <div>{x.alasan}</div>
                {x.alasan_putus && <div className="text-xs text-muted-foreground">Catatan keputusan: {x.alasan_putus}</div>}
                {x.jenis === "SAKIT" && (
                  <div className="text-xs flex flex-wrap items-center gap-2">
                    {x.bukti_url ? (
                      <button type="button" className="underline" onClick={() => lihatSurat(x)} data-testid={`izin-lihat-surat-${x.id}`}>Lihat surat dokter</button>
                    ) : (
                      <span className={butuhSurat(x) ? "text-destructive" : "text-muted-foreground"}>
                        {butuhSurat(x) ? "Surat dokter wajib — belum diunggah" : "Surat dokter tidak wajib (1 hari)"}
                      </span>
                    )}
                    {x.status === "DIAJUKAN" && (
                      <label className="cursor-pointer underline">
                        {x.bukti_url ? "Ganti surat" : "Unggah surat"}
                        <input type="file" accept={SURAT_MIME} className="hidden" disabled={sibuk}
                               onChange={(e) => { unggahSurat(x, e.target.files?.[0]); e.target.value = ""; }}
                               data-testid={`izin-unggah-surat-${x.id}`} />
                      </label>
                    )}
                  </div>
                )}
              </div>
              {x.status === "DIAJUKAN" && (
                <div className="flex gap-2">
                  {x.diajukan_oleh === profile?.id ? (
                    <>
                      {x.bisa_catat_langsung && (
                        <Button size="sm" disabled={sibuk} onClick={() => putus(x, true)} data-testid={`izin-catat-langsung-${x.id}`}>Catat langsung</Button>
                      )}
                      <Button size="sm" variant="outline" disabled={sibuk} onClick={() => batal(x)} data-testid={`izin-batal-${x.id}`}>Batalkan</Button>
                    </>
                  ) : bolehAtasNama && (
                    <>
                      <Button size="sm" disabled={sibuk} onClick={() => putus(x, true)} data-testid={`izin-setuju-${x.id}`}>Setujui</Button>
                      <Button size="sm" variant="outline" disabled={sibuk} onClick={() => putus(x, false)} data-testid={`izin-tolak-${x.id}`}>Tolak</Button>
                    </>
                  )}
                </div>
              )}
            </Card>
          ))}
          <Paginasi daftar={daftar} testId="izin-paginasi" />
        </div>
      )}
    </div>
  );
}
